import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom, map } from 'rxjs';
import { httpUrl } from '@shared/runtime-config';
import { generateId } from '@shared/id-generator';
import { AuthStore } from '@application/auth.store';
import { ImgUploadPort, UploadResult } from '@domain/ports';

const MAX_CONCURRENT_UPLOADS = 5;
const CONTAINER_NAME = 'images';
const LIMA_TIME_ZONE = 'America/Lima';

interface CommonUploadResponse {
  status?: { success: boolean; error?: unknown };
  payload?: { url: string; message?: string };
}

@Injectable({ providedIn: 'root' })
export class HttpImgUploadService extends ImgUploadPort {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthStore);

  async uploadFile(file: File): Promise<UploadResult> {
    return lastValueFrom(this.uploadFileRequest(file));
  }

  async uploadFiles(files: File[]): Promise<UploadResult[]> {
    if (files.length === 0) return [];
    return this.runWithConcurrency(files, MAX_CONCURRENT_UPLOADS);
  }

  private async runWithConcurrency(files: File[], maxConcurrent: number): Promise<UploadResult[]> {
    const results = new Array<UploadResult>(files.length);
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (true) {
        const idx = cursor++;
        if (idx >= files.length) return;
        results[idx] = await lastValueFrom(this.uploadFileRequest(files[idx]));
      }
    };
    const workers = Array.from(
      { length: Math.min(maxConcurrent, files.length) },
      () => worker()
    );
    await Promise.all(workers);
    return results;
  }

  private uploadFileRequest(file: File): Observable<UploadResult> {
    const form = new FormData();
    form.append('containerName', CONTAINER_NAME);
    form.append('blobName', this.buildBlobName(file));
    form.append('file', file, file.name);
    return this.http
      .post<CommonUploadResponse>(
        `${httpUrl('common')}/api/v1/common/files/upload`,
        form
      )
      .pipe(
        map(resp => ({
          messageId: this.generateMessageId(),
          url: resp.payload?.url ?? ''
        }))
      );
  }

  private buildBlobName(file: File): string {
    const customerRef = this.auth.currentUser()?.corporateCustomerReference ?? 'anon';
    const now = new Date();
    const dateFolder = this.formatLimaDatePart(now, { year: 'numeric', month: '2-digit', day: '2-digit' });
    const timeFile = this.formatLimaDatePart(now, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const extension = this.extractFileExtension(file.name);
    return `${customerRef}/${dateFolder}/${timeFile}-${generateId()}${extension}`;
  }

  private formatLimaDatePart(
    value: Date,
    options: Intl.DateTimeFormatOptions
  ): string {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: LIMA_TIME_ZONE,
      hour12: false,
      ...options
    });
    return formatter
      .formatToParts(value)
      .filter(part => part.type !== 'literal')
      .map(part => part.value)
      .join('');
  }

  private extractFileExtension(fileName: string): string {
    const match = /\.[a-zA-Z0-9]+$/.exec(fileName);
    return match ? match[0].toLowerCase() : '';
  }

  private generateMessageId(): string {
    return generateId();
  }
}
